/**
 * RoutinesCard — routine-health list for the OEM Home strip (Phase 2).
 *
 * A list of the workspace's routines, each with a submission-share progress bar
 * (blue engagement ramp from the Crop Circle info family) and a percentage —
 * share = this routine's submissions in the selected period / total submissions
 * across all routines in the period (so the card responds to the time selector).
 * Map-plottable: pressing the header plots assets that have routine activity,
 * coloured by routine. REAL, workspace-scoped — consumes `useHomeCards(range)`.
 */
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tokens, Skeleton, EmptyState, ErrorState } from '@gripp/shared-ui';
import { DashboardCardShell, ViewAllLink } from './dashboardCardShell';
import { ProgressBar } from './cardPrimitives';
import { useHomeCards } from '../hooks/useHomeCards';
import { HomeCardProps } from '../types';

// Routine completion → blue tier (Crop Circle info family). Darker = healthier.
function engagementColor(rate: number): string {
  if (rate >= 95) return tokens.colors.semantic.infoDark; // #1F5B99
  if (rate >= 80) return tokens.colors.semantic.info; // #4A90E2
  return `${tokens.colors.semantic.info}8C`; // muted info blue (~55%) — needs-attention
}

export const RoutinesCard: React.FC<HomeCardProps> = ({
  range,
  selected,
  onToggleSelect,
  activeStatus,
  onSelectStatus,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useHomeCards(range);
  const routines = data?.routines;

  const title = t('home.routines.title', 'Routines');
  const action = (
    <ViewAllLink
      label={t('home.cards.viewAll', 'View all')}
      onPress={() => navigate('/new-routines')}
      testID="routines-card-view-all"
    />
  );

  if (isLoading) {
    return (
      <DashboardCardShell title={title} action={action} testID="routines-card"
        loadingSlot={
          <View style={styles.loading}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} height={18} radius={4} />
            ))}
          </View>
        }
      >
        {null}
      </DashboardCardShell>
    );
  }
  if (error) {
    return (
      <DashboardCardShell title={title} action={action} testID="routines-card"
        errorSlot={<ErrorState title={t('home.cards.error', 'Could not load data')} testID="routines-card-error" />}
      >
        {null}
      </DashboardCardShell>
    );
  }
  if (!routines || routines.items.length === 0) {
    return (
      <DashboardCardShell title={title} action={action} testID="routines-card"
        emptySlot={<EmptyState title={t('home.routines.empty', 'No routines')} testID="routines-card-empty" />}
      >
        {null}
      </DashboardCardShell>
    );
  }

  return (
    <DashboardCardShell
      title={title}
      subtitle={String(routines.total)}
      action={action}
      testID="routines-card"
      onHeaderPress={onToggleSelect}
      selected={selected}
      headerPressLabel={t('home.routines.plot', 'Plot routine activity on the map')}
    >
      <ScrollView
        style={styles.list}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
      >
        {routines.items.map((r) => {
          const isActive = activeStatus === r.name;
          return (
            <Pressable
              key={r.id}
              style={[
                styles.row,
                isActive && styles.rowActive,
                activeStatus && !isActive ? styles.rowDimmed : null,
              ]}
              onPress={() => onSelectStatus?.(r.name)}
              accessibilityRole="button"
              accessibilityLabel={t('home.routines.filterBy', 'Plot {{name}} on the map', { name: r.name })}
            >
              <Text
                style={[styles.name, isActive && styles.nameActive]}
                numberOfLines={1}
              >
                {r.name}
              </Text>
              <ProgressBar value={r.completionRate} max={100} color={engagementColor(r.completionRate)} />
              <Text style={styles.pct}>{r.completionRate}%</Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </DashboardCardShell>
  );
};

RoutinesCard.displayName = 'RoutinesCard';

const styles = StyleSheet.create({
  loading: {
    gap: tokens.spacing.sm,
  },
  // -8 margin / +8 padding so the row hover/selection highlight bleeds 8px into
  // the card gutter on both sides (matches the reference -mx-2 px-2). No divider.
  list: {
    flex: 1,
    marginHorizontal: -tokens.spacing.sm,
  },
  listContent: {
    paddingHorizontal: tokens.spacing.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingVertical: tokens.spacing.sm, // py-2 (8px)
    paddingHorizontal: tokens.spacing.sm, // px-2 (8px)
    marginHorizontal: -tokens.spacing.sm, // -mx-2
    borderRadius: tokens.borderRadius.sm,
  },
  rowActive: {
    backgroundColor: tokens.colors.slate[100],
  },
  rowDimmed: {
    opacity: 0.45,
  },
  name: {
    ...tokens.typography.label.medium,
    fontSize: 13,
    lineHeight: 18,
    color: tokens.colors.neutral[800],
    flex: 1,
  },
  nameActive: {
    fontWeight: tokens.typography.label.mediumEmphasised.fontWeight,
  },
  pct: {
    ...tokens.typography.label.medium,
    fontWeight: '600',
    color: tokens.colors.slate[400],
    width: 36,
    textAlign: 'right',
    fontVariant: ['tabular-nums'],
  },
});
